// src/tabs/GuiaUsoSubTab.js
import React from "react";
import { 
  BookOpen, 
  HelpCircle, 
  ArrowRight, 
  CheckCircle, 
  Activity, 
  DollarSign, 
  Clock, 
  Layers, 
  ShieldAlert,
  AlertCircle,
  FileText,
  TrendingUp,
  ArrowRightLeft,
  Settings
} from "lucide-react";

export default function GuiaUsoSubTab() {
  return (
    <div className="flex-1 overflow-auto p-6 bg-slate-50">
      
      {/* ─── CABEÇALHO PRINCIPAL ───────────────────────────────────────── */}
      <div className="bg-slate-900 text-white rounded-2xl p-6 mb-6 shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="text-blue-400" size={24} />
            <h2 className="text-lg font-black uppercase tracking-wide">Guia do Módulo Financeiro</h2>
          </div>
          <p className="text-xs text-slate-305 max-w-2xl">
            Manual do ecossistema financeiro do Sempre Joias ERP. Este guia descreve o funcionamento das rotinas operacionais de contas a pagar, contas a receber, conciliação e fechamento contábil.
          </p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 shrink-0 text-center">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Status do Sistema</span>
          <span className="text-xs font-bold text-emerald-400">100% Homologado & Ativo</span>
        </div>
      </div>

      {/* ─── FLUXO DE TRABALHO RECOMENDADO (A ROTINA DIÁRIA) ───────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-xs">
        <h3 className="text-xs font-black uppercase text-slate-400 tracking-wider mb-5 flex items-center gap-2">
          <Activity size={14} className="text-blue-600" /> Fluxo de Trabalho Recomendado (Rotina Financeira)
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 relative">
          
          {/* Passo 1 */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="bg-blue-600 text-white font-black text-xs px-2.5 py-1 rounded-full">Passo 1</span>
                <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md uppercase tracking-wider">Origem</span>
              </div>
              <h4 className="font-bold text-xs uppercase text-slate-700 mb-2">Entradas Diretas & Staging</h4>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                As vendas do PDV, ordens de fábrica e reservas caem **direto no Ledger** com status `Pendente Conciliação` (sem fila dupla). Contas de consumo e despesas aguardam aprovação no staging de **Conferência**.
              </p>
            </div>
            <div className="flex items-center text-[10px] font-bold text-slate-400 mt-4 pt-2 border-t border-slate-200/60 gap-1.5 uppercase">
              Escrita no Livro Caixa <ArrowRight size={10} />
            </div>
          </div>

          {/* Passo 2 */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="bg-blue-600 text-white font-black text-xs px-2.5 py-1 rounded-full">Passo 2</span>
                <span className="text-[10px] font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md uppercase tracking-wider">Controle</span>
              </div>
              <h4 className="font-bold text-xs uppercase text-slate-700 mb-2">Contas a Receber & Pagar</h4>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                O operador monitora vencimentos de despesas em Contas a Pagar (suportando recorrências fixas mensais) e receitas de vendas em Contas a Receber, onde realiza baixas parciais ou finais diretamente.
              </p>
            </div>
            <div className="flex items-center text-[10px] font-bold text-slate-400 mt-4 pt-2 border-t border-slate-200/60 gap-1.5 uppercase">
              Liquidação de Títulos <ArrowRight size={10} />
            </div>
          </div>

          {/* Passo 3 */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="bg-blue-600 text-white font-black text-xs px-2.5 py-1 rounded-full">Passo 3</span>
                <span className="text-[10px] font-black text-indigo-650 bg-indigo-50 px-2 py-0.5 rounded-md uppercase tracking-wider">Banco</span>
              </div>
              <h4 className="font-bold text-xs uppercase text-slate-700 mb-2">Conciliação Inteligente</h4>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Importação do arquivo OFX bancário. A interface em duas colunas cruza extrato com o ledger e permite batimento N:M (multi-seleção), calculando taxas administrativas e aprendendo regras por descritor.
              </p>
            </div>
            <div className="flex items-center text-[10px] font-bold text-slate-400 mt-4 pt-2 border-t border-slate-200/60 gap-1.5 uppercase">
              Fechamento de Saldos <ArrowRight size={10} />
            </div>
          </div>

          {/* Passo 4 */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="bg-blue-600 text-white font-black text-xs px-2.5 py-1 rounded-full">Passo 4</span>
                <span className="text-[10px] font-black text-rose-650 bg-rose-50 px-2 py-0.5 rounded-md uppercase tracking-wider">Fechamento</span>
              </div>
              <h4 className="font-bold text-xs uppercase text-slate-700 mb-2">Trava Contábil e Relatórios</h4>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Ao fechar o mês, o período contábil é travado. Isso aciona travas de edição e estornos automáticos na alteração de registros consolidados, protegendo os relatórios do dashboard contra flutuações.
              </p>
            </div>
            <div className="flex items-center text-[10px] font-bold text-slate-400 mt-4 pt-2 border-t border-slate-200/60 gap-1.5 uppercase">
              Histórico Consolidado <CheckCircle size={10} className="text-emerald-500" />
            </div>
          </div>

        </div>
      </div>

      {/* ─── DETALHAMENTO DAS SUB-ABAS ─────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        
        {/* Contas a Pagar e Receber */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <h3 className="text-xs font-black uppercase text-slate-700 tracking-wide border-b border-slate-100 pb-3 flex items-center gap-2">
            <DollarSign className="text-emerald-600" size={16} /> Contas a Pagar & Receber
          </h3>
          
          <div className="space-y-3 text-[11px] text-slate-650">
            <div>
              <strong className="text-slate-800 block text-xs">Contas a Receber (Receitas)</strong>
              <p className="leading-relaxed">
                Centraliza todas as parcelas e receitas futuras (valor maior que zero). Exibe KPIs consolidados de provisões, valores a vencer e receitas vencidas. Ao clicar em **"Baixar"**, o operador liquida o valor no caixa definindo a data de pagamento, conta bancária de destino e valor final arrecadado.
              </p>
            </div>

            <div>
              <strong className="text-slate-800 block text-xs">Contas a Pagar (Despesas)</strong>
              <p className="leading-relaxed">
                Centraliza despesas agendadas. Oferece suporte a **Despesas Fixas Recorrentes** (onde o operador agenda repetições automáticas mensais até uma data-limite) e vincula tipos de compromissos específicos.
              </p>
            </div>

            <div>
              <strong className="text-slate-850 block text-[10px] uppercase font-black text-blue-600">Filtros Dinâmicos de Período e Situação:</strong>
              <p className="leading-relaxed">
                Ambas as sub-abas contam com seletores rápidos de período (Data Início/Fim) e situação contábil (Apenas Pendentes, Apenas Conciliadas, Todas) para buscas eficientes na base.
              </p>
            </div>
          </div>
        </div>

        {/* Conciliação e Conferência */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-4">
          <h3 className="text-xs font-black uppercase text-slate-700 tracking-wide border-b border-slate-100 pb-3 flex items-center gap-2">
            <ArrowRightLeft className="text-indigo-650" size={16} /> Conciliação Bancária & Fila de Conferência
          </h3>
          
          <div className="space-y-3 text-[11px] text-slate-655">
            <div>
              <strong className="text-slate-800 block text-xs">Conciliação de Duas Colunas (N:M)</strong>
              <p className="leading-relaxed">
                Interface de batimento direto. A coluna da esquerda exibe os lançamentos pendentes no Ledger e a coluna da direita traz as linhas do extrato importado. 
              </p>
              <ul className="list-disc pl-4 mt-1 space-y-1 text-slate-500">
                <li><strong className="text-slate-700">Seleção Múltipla</strong>: Marque N lançamentos e cruze com M transações do extrato de uma só vez.</li>
                <li><strong className="text-slate-700">Ajuste Automático de Taxas (R4b)</strong>: A diferença matemática entre os lados é calculada ao vivo. Se houver diferença (Soma Ledger maior que Soma Banco), o sistema cria automaticamente um lançamento de despesa na categoria **"TAXAS DE CARTÃO"** ou **"TARIFAS BANCÁRIAS"** acoplado ao grupo.</li>
              </ul>
            </div>

            <div>
              <strong className="text-slate-800 block text-xs">Memória Automática por Descritor (R7)</strong>
              <p className="leading-relaxed">
                Sempre que você conciliar manualmente uma transação sem regra cadastrada, o sistema sugere memorizar o padrão. Nas próximas importações, descritores idênticos (ex: "REDECD", "PIX RECEIVED") exibem badges azuis de recomendação e pré-selecionam a categoria sugerida nos formulários de pendência de staging.
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* ─── REGRAS E BOAS PRÁTICAS (INVARIANTES E DICAS) ───────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        
        {/* Trava Progressiva de Edição */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-3">
          <h4 className="text-xs font-black uppercase text-slate-700 tracking-wide flex items-center gap-1.5">
            <ShieldAlert size={14} className="text-rose-650" /> Trava Progressiva de Edição (R1)
          </h4>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Garante a segurança e a conformidade contábil do Livro Caixa:
          </p>
          <ul className="text-[11px] text-slate-650 space-y-2">
            <li>
              <strong>Modo Rascunho (Meses Abertos/Pendentes):</strong> É permitido alterar livremente qualquer informação de lançamentos não conciliados.
            </li>
            <li>
              <strong>Modo Histórico (Meses Fechados ou Conciliados):</strong> Os campos de valor, data e credor/descrição ficam bloqueados. Se alterados, o ERP executa automaticamente sob o capô um lançamento de **Estorno** (anulação) e relança a linha corrigida.
            </li>
            <li>
              <strong>Reclassificação Livre:</strong> Alterar categoria, centro de custo ou tipo de compromisso é livre a qualquer momento para re-apuração gerencial.
            </li>
          </ul>
        </div>

        {/* Exibição e Ocultação de Estornos */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-3">
          <h4 className="text-xs font-black uppercase text-slate-750 tracking-wide flex items-center gap-1.5">
            <ArrowRightLeft size={14} className="text-slate-500" /> Controle de Estornos (R11)
          </h4>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Para manter a visualização do Livro Caixa limpa e produtiva:
          </p>
          <ul className="text-[11px] text-slate-650 space-y-1.5 list-disc pl-4">
            <li>Por padrão, as linhas de estorno e os lançamentos correspondentes que foram anulados ficam **ocultos** na listagem.</li>
            <li>Para auditar, marque a opção **"Mostrar Estornos"** no cabeçalho de busca. Lançamentos anulados exibirão uma badge vermelha **"ESTORNADO"** e texto riscado.</li>
            <li>Relatórios consolidados computam ambos normalmente (a ocultação é estritamente visual).</li>
          </ul>
        </div>

        {/* Financiamentos e Investimentos */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs space-y-3">
          <h4 className="text-xs font-black uppercase text-slate-750 tracking-wide flex items-center gap-1.5">
            <Layers size={14} className="text-blue-600" /> Financiamentos & Investimentos (R12)
          </h4>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Permite acompanhar contratos ativos e planejamento futuro na aba Financiamentos:
          </p>
          <ul className="text-[11px] text-slate-655 space-y-2">
            <li>
              <strong>Divisão Principal vs Juros:</strong> Ficha descritiva separando a amortização da dívida real dos juros consolidados.
            </li>
            <li>
              <strong>Investimentos Programados:</strong> Planeje e categorize alocações futuras de capital (ex: aquisição de novas máquinas, computadores ou maquinários de fábrica).
            </li>
          </ul>
        </div>

      </div>

      {/* ─── DICAS E SOLUÇÃO DE DÚVIDAS FREQUENTES ──────────────────────── */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
        <h4 className="text-xs font-black uppercase text-blue-800 tracking-wide mb-3 flex items-center gap-1.5">
          <AlertCircle size={14} /> Dúvida Frequente: "Como funciona a diferença/taxa de cartão no rodapé?"
        </h4>
        <p className="text-[11px] text-slate-750 leading-relaxed mb-3">
          Ao marcar, por exemplo, 5 lançamentos de vendas no Ledger (digamos que somem R$ 1.000,00) e selecionar o depósito correspondente no banco (que veio líquida a R$ 970,00), a barra exibirá: **Ledger: R$ 1000, Extrato: R$ 970, Diferença/Taxa: R$ 30**. 
          Ao clicar em **Conciliar Grupo**, o sistema conciliará todas as linhas e gerará automaticamente um novo lançamento de despesa de R$ -30,00 associado à categoria "TAXAS DE CARTÃO", zerando o caixa e documentando o encargo cobrado pela adquirente.
        </p>
      </div>

    </div>
  );
}
